import DevCycleProvider from '@devcycle/openfeature-web-provider'
import { EvaluationContextValue, OpenFeature, OpenFeatureProvider } from '@openfeature/react-sdk'
import { Loader } from '@tom-ui/ui'
import { FC, ReactNode, useEffect, useState } from 'react'
import { useAppConfiguration } from './AppConfigurationProvider'

export type FeatureFlagContext = {
  user_id: string
  customData: {
    tenantId: string
  }
} & Record<string, EvaluationContextValue>

export const FeatureFlagProvider: FC<{ children: ReactNode; context: FeatureFlagContext }> = ({
  children,
  context,
}) => {
  const { devCycleProviderSdkKey } = useAppConfiguration()
  const [initialized, setInitialized] = useState(false)

  // Initialise the DevCycle provider ONCE. Recreating it on every `context`
  // change (context is a fresh object each render) re-ran setProviderAndWait in
  // a tight loop -> thousands of sdkConfig fetches, hung tab. Provider setup
  // depends only on the SDK key.
  useEffect(() => {
    if (!devCycleProviderSdkKey) return
    let cancelled = false
    const init = async () => {
      OpenFeature.setContext(context)
      await OpenFeature.setProviderAndWait(new DevCycleProvider(devCycleProviderSdkKey))
      if (!cancelled) setInitialized(true)
    }
    init()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devCycleProviderSdkKey])

  // Push context updates without re-creating the provider.
  useEffect(() => {
    if (initialized) OpenFeature.setContext(context)
  }, [context, initialized])

  if (!initialized) return <Loader show />

  return <OpenFeatureProvider>{children}</OpenFeatureProvider>
}
